/* eslint-disable jsx-a11y/no-distracting-elements */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { loadTranslations } from '../i18n';
import { API_URL, ENDPOINTS } from '../constants/api';
import { decodeToken, getToken } from '../utils/TokenUtils';
import ModalForm from './ModalForm';
import TopNavbarStatement from './TopNavbarStatement';
import { notification } from 'antd';
import InfoModal from './InfoModal';
import FeatherIcon from 'feather-icons-react';
import '../css/navbar.css';
import PasswordWarning from '../pages/users/PasswordWarning';
import LayoutSwitcher from './LayoutSwitcher';
import SessionWarning from './SessionWarning';
import { useSessionTimeout } from '../utils/useSessionTimeout';

const TopBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = getToken();

  const userId = decodeToken(token, 'id');
  const userName = decodeToken(token, 'name');
  const userImage = decodeToken(token, 'image');
  const companyName = decodeToken(token, 'companyName');
  const companyLogo = decodeToken(token, 'logo');
  const loginPage = decodeToken(token, 'loginPage');
  const dashboardPage = decodeToken(token, 'dashboardPage');
  const userlevel = decodeToken(token, 'userLevel');
  const passwordExpireLeft = decodeToken(token, 'passwordExpireLeft');
  const userType = decodeToken(token, 'userType');

  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState(localStorage.getItem('language'));
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({
    userId: null,
    token: null,
    route: null,
  });

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showWarning,INACTIVE_TIME, secondsLeft, stayLoggedIn } = useSessionTimeout(token);
  // 🔹 when click on a search result
  const onClickSearch = (e, path, route, tab) => {
    e.preventDefault();
    setQuery('');
    setResults([]);
    // console.log(path, route);
    if (path.includes('-modal/')) {
      setModalProps({ userId, token, route });
      setIsModalOpen(true);
    } else {
      // Navigate to the new path
      if (tab === 'new-tab') {
        const fullPath = path.startsWith('http')
          ? path
          : `${window.location.origin}/${path}`;
        window.open(fullPath, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    }
  };

  useEffect(() => {
    if (query.length === 0) {
      setResults([]);
      return;
    }

    if (!token) {
      navigate('/login/user-login');
    }
    const fetchData = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.SYS}/search-link?query=${query}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          }
        );

        //  console.log(response.data);
        setResults(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query]);

  // Fetch languages and notifications
  useEffect(() => {
    loadTranslations();

    const fetchLanguages = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.USER}/get-languages`
        );
        setLanguages(response.data);
      } catch (error) {
        notification.error({ message: 'Error fetching languages' });
        console.error(error);
      }
    };

    const fetchNotifications = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/${ENDPOINTS.SYS}/reportCustom/unread-notifications`,
          { user_level: userlevel, user_id: userId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotifications(response.data.data);
        setUnreadNotifications(response.data.data[0]?.count || 0);
      } catch (error) {
        //notification.error({ message: 'Error fetching notifications' });
        console.error(error);
      }
    };

    fetchLanguages();
    fetchNotifications();
  }, [token, userId, userlevel]);

  const handleLanguageChange = (e) => {
    localStorage.setItem('language', e.target.value);
    window.location.reload();
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API_URL}/api/users/logout`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('authToken');
      navigate(`/login/${loginPage}`);
    } catch (error) {
      console.error(error);
      localStorage.removeItem('authToken');
      navigate(`/login/${loginPage}`);
    }
  };

  const handleOpenModal = (e, route) => {
    e.preventDefault();
    setModalProps({ userId, token, route });
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleSelectedNotification = (id, index) => {
    setShowNotifications(false);
    setSelectedNotification(notifications[index]);
  };

  if (!token) return null;

  return (
    <>
      {showWarning && (
      <SessionWarning
        secondsLeft={secondsLeft}
        totalSeconds={INACTIVE_TIME}
        stayLoggedIn={stayLoggedIn}
      />
    )}
      <div className="fixed-top">
        {/* Top statement bar with language */}
        <nav className="navbar top-navbar">
          <TopNavbarStatement />
          <LayoutSwitcher />
          <select
            className="language-dropdown"
            value={language}
            onChange={handleLanguageChange}
          >
            {languages?.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {t(lang.name)}
              </option>
            ))}
          </select>
        </nav>

        {/* Main navbar */}
        <nav className="navbar navbar-expand-lg navbar-light bg-light">
          <div className="container-fluid">
            <img
              src={`${API_URL}/${companyLogo}`}
              width="50"
              alt="Company Logo"
              className="rounded-circle"
            />{' '}
            &nbsp;
            <a
              className="navbar-brand"
              href={`/${dashboardPage?.replace('/', '')}`}
            >
              {companyName}
            </a>
            {userType === 'user' && (
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control" // Bootstrap class for styling
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  style={{
                    width: '100%', // Ensure it matches the container width
                  }}
                />
                {/* {loading && <div>Loading...</div>} */}
                {results && results.length > 0 && (
                  <ul
                    className="list-group position-absolute"
                    style={{
                      margin: 0,
                      top: '100%',
                      width: '100%',
                      zIndex: 1,
                      maxHeight: '550px', // Limit height
                      overflowY: 'auto', // Add vertical scroll
                      border: '1px solid #ccc', // Add border for better visibility
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                    }}
                  >
                    {results.map((item, index) => (
                      <li
                        className="list-group-item"
                        key={index}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        <small>
                          {/* {item.name} */}
                          {t(item.name)}
                          {/* <span className="badge bg-danger">
                                        {' '}
                                        {item?.description}
                                      </span> */}
                        </small>
                        <br />
                        <a
                          href="/"
                          onClick={(e) =>
                            onClickSearch(
                              e,
                              item.full_route,
                              item.route,
                              'current-tab'
                            )
                          }
                        >
                          Click
                        </a>
                        &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                        <a
                          href="/"
                          onClick={(e) =>
                            onClickSearch(
                              e,
                              item.full_route,
                              item.route,
                              'new-tab'
                            )
                          }
                        >
                          New Tab
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Notifications */}
            <ul className="navbar-nav ms-auto">
              <li className="nav-item dropdown position-relative">
                <a
                  href="#"
                  className="nav-link position-relative"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowNotifications(!showNotifications);
                  }}
                  title="Notifications"
                >
                  <FeatherIcon icon="bell" />
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: '0.6rem' }}
                  >
                    {unreadNotifications}
                  </span>
                </a>
                {showNotifications && (
                  <div
                    className="dropdown-menu show position-absolute mt-2"
                    style={{ right: 0, minWidth: '250px', zIndex: 1000 }}
                  >
                    {notifications?.map((notification, index) => (
                      <button
                        className="dropdown-item"
                        key={index}
                        onClick={() =>
                          handleSelectedNotification(notification.id, index)
                        }
                      >
                        {notification.title}
                      </button>
                    ))}
                  </div>
                )}
              </li>

              {/* User profile */}
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="/"
                  id="userDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img
                    src={`${API_URL}/${userImage}`}
                    alt="User"
                    className="rounded-circle"
                    style={{ width: '30px', height: '30px' }}
                  />{' '}
                  &nbsp; {userName}
                </a>
                <ul
                  className="dropdown-menu dropdown-menu-end"
                  aria-labelledby="userDropdown"
                >
                  <li>
                    <Link className="dropdown-item" to="/profile">
                      {t('Profile')}
                    </Link>
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="/"
                      onClick={(e) => handleOpenModal(e, 'change-password')}
                    >
                      {t('Change Password')}
                    </a>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <a
                      className="dropdown-item"
                      href="/"
                      onClick={handleLogout}
                    >
                      {t('Logout')}
                    </a>
                  </li>
                  {userlevel === 'developer' && (
                    <>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <a className="dropdown-item" href="/dashboard">
                          Backup
                        </a>
                      </li>
                    </>
                  )}
                </ul>
              </li>
            </ul>
          </div>
        </nav>
      </div>
      {/* Password warning */}
      {/* <PasswordWarning
        passwordExpireLeft={passwordExpireLeft}
        handleOpenModal={handleOpenModal}
      /> */}
      {/* Password expiry warning */}
      {/* {passwordExpireLeft !== null && passwordExpireLeft < 15 && (
        <p className="text-danger text-center fw-bold">
          <marquee>
            Your password will expire soon, it only remains {passwordExpireLeft}{' '}
            days, please{' '}
            <a href="/" onClick={(e) => handleOpenModal(e, 'change-password')}>
              change
            </a>{' '}
            it before expires.
          </marquee>
        </p>
      )} */}

      {/* Modals */}
      {selectedNotification?.id && (
        <InfoModal
          title={selectedNotification.title}
          info={selectedNotification.message}
          onclose={() => setSelectedNotification({})}
        />
      )}
      {modalProps?.route && (
        <ModalForm
          userId={modalProps.userId}
          token={modalProps.token}
          route={modalProps.route}
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        />
      )}
    </>
  );
};

export default TopBar;
